import React, { useRef, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import * as quillEmoji from "@windmillcode/quill-emoji";
import "react-quill-new/dist/quill.snow.css";
import "@windmillcode/quill-emoji/quill-emoji.css";

const defaultEditorStyleId = "react-text-editor-default-style";
const defaultEditorStyleCss = `
.react-text-editor .ql-editor {
  font-size: 12px;
  line-height: 16px;
}
`;

const EmojiBlot = quillEmoji.EmojiBlot?.default ?? quillEmoji.EmojiBlot;
const ShortNameEmoji =
  quillEmoji.ShortNameEmoji?.default ?? quillEmoji.ShortNameEmoji;
const ToolbarEmoji =
  quillEmoji.ToolbarEmoji?.default ?? quillEmoji.ToolbarEmoji;
const TextAreaEmoji =
  quillEmoji.TextAreaEmoji?.default ?? quillEmoji.TextAreaEmoji;

Quill.register(
  {
    "formats/emoji": EmojiBlot,
    "modules/emoji-shortname": ShortNameEmoji,
    "modules/emoji-toolbar": ToolbarEmoji,
    "modules/emoji-textarea": TextAreaEmoji,
  },
  true,
);

const formats = [
  "font",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "color",
  "background",
  "list",
  "indent",
  "align",
  "link",
  "clean",
  "emoji",
];

const toolbarOptions = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
  "emoji-toolbar": true,
  "emoji-textarea": true,
  "emoji-shortname": true,
};

export default function ReactTextEditor({
  value,
  handleChange,
  handleBlur,
  defaultValue,
  ...rest
}) {
  const quillRef = useRef(null);
  const initialValue = value ?? defaultValue ?? "";
  const lastEmittedValueRef = useRef(initialValue);
  const pendingValueRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastKnownSelectionRef = useRef(null);

  useEffect(() => {
    const nextValue = value ?? defaultValue ?? "";
    lastEmittedValueRef.current = nextValue;
    pendingValueRef.current = null;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [value, defaultValue]);

  useEffect(() => {
    const existingStyle = document.getElementById(defaultEditorStyleId);
    if (!existingStyle) {
      const styleEl = document.createElement("style");
      styleEl.id = defaultEditorStyleId;
      styleEl.textContent = defaultEditorStyleCss;
      document.head.appendChild(styleEl);
    }

    const quill = quillRef.current?.getEditor?.() || quillRef.current?.editor;
    if (!quill) return;

    const toolbarEl = quill.container.querySelector(".ql-toolbar");
    const parentForm = quill.container.closest("form");

    const isEditorUiElement = (element) => {
      if (!element || !(element instanceof Element)) return false;
      return Boolean(
        element.closest(
          ".react-text-editor, .ql-toolbar, #emoji-palette, #textarea-emoji",
        ),
      );
    };

    const forceButtonType = (btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.getAttribute("type") !== "button") {
        btn.setAttribute("type", "button");
      }
    };

    const enforceButtonsNonSubmit = (root) => {
      if (!root) return;
      if (root instanceof HTMLButtonElement) {
        forceButtonType(root);
      }
      root.querySelectorAll?.("button")?.forEach(forceButtonType);
    };

    const enforceToolbarButtonsNonSubmit = () => {
      enforceButtonsNonSubmit(toolbarEl);
    };

    // Quill/emoji plugin toolbar buttons are rendered inside a <form>; force them to non-submit buttons.
    enforceToolbarButtonsNonSubmit();

    const toolbarClickGuard = (event) => {
      const targetButton = event.target?.closest?.("button");
      if (!targetButton) return;
      forceButtonType(targetButton);
    };

    toolbarEl?.addEventListener("click", toolbarClickGuard);

    const submitGuard = (event) => {
      const submitter = event.submitter;
      const activeElement = document.activeElement;

      if (isEditorUiElement(submitter) || isEditorUiElement(activeElement)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    parentForm?.addEventListener("submit", submitGuard, true);

    const toolbarObserver = new MutationObserver(() => {
      enforceToolbarButtonsNonSubmit();
    });

    if (toolbarEl) {
      toolbarObserver.observe(toolbarEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["type"],
      });
    }

    const decodeEmojiUnicode = (emojiValue) => {
      if (!emojiValue) return emojiValue;

      const parts = String(emojiValue)
        .split("-")
        .map((part) => Number.parseInt(part, 16));
      if (parts.some(Number.isNaN)) return emojiValue;

      try {
        return String.fromCodePoint(...parts);
      } catch {
        return emojiValue;
      }
    };

    const hydrateFlagGlyphs = (panelEl) => {
      panelEl.querySelectorAll(".bem.ap").forEach((node) => {
        const raw = node.textContent?.trim();
        if (!raw) return;

        if (/^[0-9a-f]+(?:-[0-9a-f]+)+$/i.test(raw)) {
          const decoded = decodeEmojiUnicode(raw);
          if (decoded !== raw) {
            node.textContent = decoded;
          }
        }
      });
    };

    const positionPanel = (panelEl) => {
      const selection = quill.getSelection();
      if (!selection) return;

      const caret = quill.getBounds(selection.index);
      const editorRect = quill.container.getBoundingClientRect();

      const panelWidth = 320;
      const panelHeight = 240;
      const gap = 8;

      const caretLeft = editorRect.left + caret.left;
      const caretTop = editorRect.top + caret.top + caret.height;

      let left = caretLeft + gap;
      let top = caretTop + gap;

      if (left + panelWidth > window.innerWidth - gap) {
        left = caretLeft - panelWidth - gap;
      }

      if (top + panelHeight > window.innerHeight - gap) {
        top = editorRect.top + caret.top - panelHeight - gap;
      }

      if (left < gap) left = gap;
      if (top < gap) top = gap;

      panelEl.style.position = "fixed";
      panelEl.style.left = `${left}px`;
      panelEl.style.top = `${top}px`;
      panelEl.style.width = `${panelWidth}px`;
      panelEl.style.height = `${panelHeight}px`;
      panelEl.style.maxHeight = `${panelHeight}px`;
      panelEl.style.zIndex = "9999";
      panelEl.style.background = "#fff";
      panelEl.style.overflow = "hidden";

      hydrateFlagGlyphs(panelEl);
    };

    let selectionChangeHandler = null;

    const observePanel = (panelEl) => {
      requestAnimationFrame(() => positionPanel(panelEl));
      enforceButtonsNonSubmit(panelEl);

      if (selectionChangeHandler) {
        quill.off("selection-change", selectionChangeHandler);
      }

      selectionChangeHandler = () => {
        requestAnimationFrame(() => positionPanel(panelEl));
      };

      quill.on("selection-change", selectionChangeHandler);

      const panelObserverLocal = new MutationObserver(() => {
        enforceButtonsNonSubmit(panelEl);
        hydrateFlagGlyphs(panelEl);
        requestAnimationFrame(() => positionPanel(panelEl));
      });

      panelObserverLocal.observe(panelEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      const closeBtn = document.createElement("button");
      closeBtn.setAttribute("type", "button");
      closeBtn.setAttribute("aria-label", "Close emoji panel");
      closeBtn.textContent = "✕";
      Object.assign(closeBtn.style, {
        position: "absolute",
        top: "6px",
        right: "6px",
        zIndex: "10001",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: "16px",
        padding: "2px",
      });
      panelEl.style.position = panelEl.style.position || "fixed";
      panelEl.appendChild(closeBtn);

      const cleanup = () => {
        try {
          panelObserverLocal.disconnect();
        } catch {}
        try {
          closeBtn.removeEventListener("click", onClose);
        } catch {}
        try {
          document.removeEventListener("mousedown", outsideClickHandler);
        } catch {}
        try {
          quill.off("selection-change", selectionChangeHandler);
        } catch {}
        try {
          panelEl.remove();
        } catch {
          panelEl.style.display = "none";
        }
      };

      const onClose = (e) => {
        if (e) e.stopPropagation();
        cleanup();
      };

      closeBtn.addEventListener("click", onClose);

      const outsideClickHandler = (ev) => {
        if (!panelEl.contains(ev.target)) onClose();
      };

      setTimeout(
        () => document.addEventListener("mousedown", outsideClickHandler),
        0,
      );

      return { observer: panelObserverLocal, cleanup };
    };

    let panelObserver = null;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          enforceButtonsNonSubmit(node);

          if (node.id === "textarea-emoji" || node.id === "emoji-palette") {
            if (panelObserver?.cleanup) panelObserver.cleanup();
            panelObserver = observePanel(node);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const positionLinkTooltip = (tooltipEl) => {
      if (!tooltipEl || tooltipEl.classList.contains("ql-hidden")) return;

      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      const range =
        quill.getSelection() ||
        lastKnownSelectionRef.current ||
        quill.selection?.savedRange;
      if (!range) return;

      const containerEl = quill.container;
      const editorEl = quill.root;
      const gap = 8;
      const containerWidth = containerEl.clientWidth;
      const containerHeight = containerEl.clientHeight;
      const tooltipWidth = tooltipEl.offsetWidth || 240;
      const tooltipHeight = tooltipEl.offsetHeight || 44;

      let bounds = quill.getBounds(range.index, range.length);
      let left = bounds.left;
      if (left + tooltipWidth > containerWidth - gap) {
        left = containerWidth - tooltipWidth - gap;
      }
      if (left < gap) left = gap;

      let top = bounds.bottom + gap;
      const maxTop = Math.max(gap, containerHeight - tooltipHeight - gap);

      // Keep the tooltip below the selection by scrolling editor content if needed.
      if (top > maxTop) {
        editorEl.scrollTop += top - maxTop;
        bounds = quill.getBounds(range.index, range.length);
        top = bounds.bottom + gap;
      }

      if (top > maxTop) top = maxTop;

      tooltipEl.style.position = "absolute";
      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.top = `${top}px`;
      tooltipEl.style.transform = "none";

      // Keep the tooltip above surrounding form controls.
      tooltipEl.style.zIndex = "99999";
    };

    const tooltipCleanups = [];
    const linkSelectionHandler = (range) => {
      if (range) {
        lastKnownSelectionRef.current = range;
      }
    };
    quill.on("selection-change", linkSelectionHandler);

    const observeTooltip = (tooltipEl) => {
      if (tooltipEl.dataset.linkObserved === "1") return;
      tooltipEl.dataset.linkObserved = "1";

      // Quill toggles `ql-hidden` class and `data-mode` attr to show/hide the tooltip.
      // Watch both so we reposition as soon as it becomes visible.
      const mo = new MutationObserver(() => {
        requestAnimationFrame(() => positionLinkTooltip(tooltipEl));
      });
      mo.observe(tooltipEl, {
        attributes: true,
        attributeFilter: ["class", "data-mode", "style"],
      });

      const resizeHandler = () =>
        requestAnimationFrame(() => positionLinkTooltip(tooltipEl));
      window.addEventListener("resize", resizeHandler);
      window.addEventListener("scroll", resizeHandler, true);

      const cleanup = () => {
        try {
          mo.disconnect();
        } catch {}
        window.removeEventListener("resize", resizeHandler);
        window.removeEventListener("scroll", resizeHandler, true);
      };

      tooltipCleanups.push(cleanup);
      tooltipEl._cleanup = cleanup;
    };

    // Quill creates .ql-tooltip once at init and reuses it — wire it immediately.
    const existingTooltip = quill.container.querySelector(".ql-tooltip");
    if (existingTooltip) observeTooltip(existingTooltip);

    // Fallback: handle any dynamic recreation.
    const tooltipObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const tt = node.classList?.contains("ql-tooltip")
            ? node
            : node.querySelector?.(".ql-tooltip");
          if (tt) observeTooltip(tt);
        }
      }
    });
    tooltipObserver.observe(quill.container, {
      childList: true,
      subtree: true,
    });

    return () => {
      parentForm?.removeEventListener("submit", submitGuard, true);
      toolbarEl?.removeEventListener("click", toolbarClickGuard);
      toolbarObserver.disconnect();
      observer.disconnect();
      if (panelObserver?.cleanup) panelObserver.cleanup();
      else panelObserver?.observer?.disconnect?.();
      tooltipObserver.disconnect();
      tooltipCleanups.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
      quill.off("selection-change", linkSelectionHandler);

      if (selectionChangeHandler) {
        quill.off("selection-change", selectionChangeHandler);
      }
    };
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const flushPending = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingValueRef.current !== null && handleChange) {
      handleChange(pendingValueRef.current);
      pendingValueRef.current = null;
    }
  };

  const handleEditorChange = (content, _delta, source) => {
    if (source !== "user") return;
    if (content === lastEmittedValueRef.current) return;

    lastEmittedValueRef.current = content;
    pendingValueRef.current = content;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      if (pendingValueRef.current !== null && handleChange) {
        handleChange(pendingValueRef.current);
        pendingValueRef.current = null;
      }
    }, 300);
  };

  return (
    <div className="react-text-editor">
      <ReactQuill
        value={initialValue}
        ref={(el) => (quillRef.current = el)}
        theme="snow"
        modules={toolbarOptions}
        formats={formats}
        onBlur={(range, source, editor) => {
          flushPending();
          if (handleBlur) handleBlur(range, source, editor);
        }}
        onChange={handleEditorChange}
        {...rest}
      />
    </div>
  );
}
